// ================================================================
// apiClient — die EINE Quelle für authentifizierte Fetch-Aufrufe + Fehlerbehandlung.
// ================================================================
// Ersetzt 16 fast identische Kopien derselben ~15 Zeilen (auth.js, customers.js,
// documents.js, email.js, oauth.js, org.js, platform.js, projects.js, runner.js,
// scenarios.js, timeEntries.js, whatsapp.js, prompts-store.js, credentials-store.js,
// SettingsModal.js) — jede mit eigenen kleinen Abweichungen, wie ein Fehler aus dem
// Response-Body gelesen wird.
//
// Der eigentliche Grund für dieses Modul: das Backend liefert Fehler jetzt einheitlich
// als { error: { code, message, details? } } (docs/i18n.md, Regel 4 — "Server liefert
// Key/Code, Frontend übersetzt"). Übersetzt wird HIER, EINMAL: err.message ist ab jetzt
// der lokalisierte Text (t('errors.<code>'), wenn der Code eine Übersetzung hat — sonst
// der deutsche Server-Text als Fallback fürs Log/für unbekannte Codes). Jeder
// bestehende Aufrufer, der `err.message || t('eigener.fallback')` macht, funktioniert
// dadurch unverändert weiter, zeigt aber jetzt den übersetzten statt immer den
// deutschen Text.
//
// NICHT für Datei-Uploads (FormData) — die brauchen eigene Header (kein
// Content-Type: application/json, der Multipart-Boundary kommt vom Browser). Diese
// Stellen (org.js uploadAttachment/uploadLetterheadBackground, documents.js
// uploadLogo) bleiben eigene kleine fetch-Aufrufe, lesen den Fehler aber genauso
// über readErrorMessage() unten.

import { t } from '../i18n/index.js';
// signedOut.js hängt an NICHTS — darum kein Ringschluss, obwohl auth.js diese
// Datei nutzt und diese Datei den Rauswurf-Grund schreibt.
import { rememberSignOut } from './signedOut.js';

const TOKEN_KEY = 'levelupwerk:token';

function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Liest die lokalisierte Fehlermeldung aus einem geparsten Response-Body.
 * Erwartet die Standardform { error: { code, message, details? } }; für Robustheit
 * (Netzwerkfehler, unerwartete Antworten) fällt sie sauber auf 'HTTP <status>' zurück.
 */
export function readErrorMessage(body, status) {
  const code = body?.error?.code;
  const serverMessage = body?.error?.message;
  if (code) {
    const translated = t('errors.' + code);
    // t() gibt den Key selbst zurück, wenn er fehlt — daran erkennen wir einen
    // unbekannten Code und fallen auf den Server-Text zurück statt den rohen Key zu zeigen.
    if (translated !== 'errors.' + code) return translated;
  }
  return serverMessage || t('errors.GENERIC') || ('HTTP ' + status);
}

/**
 * Authentifizierter JSON-Request. Wirft bei !res.ok einen Error mit lokalisierter
 * .message, plus .status (HTTP-Statuscode) und .code (Server-Fehlercode, für Aufrufer,
 * die gezielt auf eine bestimmte Bedeutung reagieren müssen — z.B. 'CUSTOMER_HAS_
 * DOCUMENTS'). .serverMessage trägt den rohen deutschen Text fürs Debuggen/Log,
 * NIE für die UI verwenden.
 *
 * @param {string} path
 * @param {RequestInit & { auth?: boolean }} opts  auth: false unterdrückt den
 *   Authorization-Header (z.B. Login/Register, bevor ein Token existiert).
 */
// Single-Flight für den Token-Refresh: die Rotation macht den alten Refresh-Token
// sofort ungültig — würden zwei parallel abgelaufene Requests beide /api/auth/refresh
// aufrufen, käme der zweite mit dem schon rotierten Token an und die Session stürbe.
// Deshalb teilen sich alle gleichzeitigen 401er EINEN Refresh-Versuch.
// (auth.js wird dynamisch importiert, weil es selbst apiRequest importiert — Zyklus.)
let refreshInFlight = null;

function refreshOnce() {
  if (!refreshInFlight) {
    refreshInFlight = import('./auth.js')
      .then((auth) => auth.tryRefresh())
      .catch(() => false)
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

export async function apiRequest(path, opts = {}) {
  const { auth = true, _retried = false, ...rest } = opts;
  const token = auth ? getToken() : null;
  const res = await fetch(path, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
      ...(rest.headers || {}),
    },
  });

  let body = {};
  try { body = await res.json(); } catch { /* Antwort ohne Body */ }

  if (!res.ok) {
    // Abgelaufener Access-Token (12h, CASA 2.2.3): einmal still refreshen und den
    // Request wiederholen. Nur für authentifizierte Aufrufe — Login/Refresh/Logout
    // laufen mit auth:false und können hier nie in eine Schleife geraten.
    // AUSNAHME: wurde die Sitzung ENTZOGEN («ein Konto, ein Gerät»), ist ein
    // Refresh-Versuch sinnlos — der Server lehnt auch das Refresh-Token ab.
    // Schlimmer: der Versuch würde den Grund verschlucken und aus dem Rauswurf
    // ein gewöhnliches «Token abgelaufen» machen.
    const revoked = typeof body?.error?.code === 'string' && body.error.code.startsWith('AUTH_SESSION_REVOKED');
    if (res.status === 401 && auth && !_retried && !revoked) {
      if (await refreshOnce()) {
        return apiRequest(path, { ...opts, _retried: true });
      }
    }
    const err = new Error(readErrorMessage(body, res.status));
    err.status = res.status;
    err.code = body?.error?.code;
    err.serverMessage = body?.error?.message;
    err.details = body?.error?.details;
    // Von einem anderen Gerät abgemeldet: den Grund merken (er muss einen
    // App-Neustart überleben) und die App zurück an die Anmeldemaske schicken.
    // Zentral hier und nicht in jedem Aufrufer — es kann JEDE Anfrage treffen.
    if (revoked) {
      rememberSignOut(err.code, err.details?.revokedByDevice ?? null);
      window.dispatchEvent(new CustomEvent('lw:session-revoked'));
    }
    // Plattform-Konten ohne zweiten Faktor sperrt der Server aus (requirePlatformMfa).
    // Die Einrichtung liegt sonst nur im Firmen-Dashboard, das diese Konten nie
    // sehen — ohne diesen Hinweis wäre die Fehlermeldung eine Sackgasse.
    if (err.code === 'MFA_SETUP_REQUIRED') {
      window.dispatchEvent(new CustomEvent('lw:mfa-setup-required'));
    }
    throw err;
  }
  return body;
}
